import { DateTime } from "luxon";
import { makeAutoObservable } from "mobx";
import { z } from "zod";
import invariant from "invariant";
import { User } from "../../user/index.js";
import { config } from "../../config/config.js";

export type AuthJson = z.infer<typeof Auth.schema>;

export class Auth {
  public readonly user: User;
  public readonly accessToken: string;
  public readonly refreshToken: string;
  public readonly _expiry: Date;

  public static readonly schema = z.object({
    userId: z.string(),
    accessToken: z.string(),
    refreshToken: z.string(),
    expiry: z.string(),
  });

  public constructor(init: AuthJson) {
    const { accessToken, refreshToken, expiry, userId } = init;
    this.user = User.ofId(userId);
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this._expiry = new Date(expiry);
    makeAutoObservable(this);
  }

  public static async login(email: string, password: string) {
    const response = await config.behaviors.user.authenticate({
      email,
      password,
    });

    if (response === "mfaRequired") {
      return response;
    }

    const userResponse = await config.behaviors.user.find(
      "self",
      response.token,
    );

    invariant(userResponse, "user not found");

    return new Auth({
      userId: userResponse.userId,
      accessToken: response.token,
      refreshToken: response.refreshToken,
      expiry: response.expires,
    });
  }

  public async logout() {
    await config.behaviors.user.logout();
  }

  public get expiry() {
    return DateTime.fromJSDate(this._expiry);
  }

  public isRefreshRequired() {
    return DateTime.now() > this.expiry.minus({ minutes: 5 });
  }

  public async refresh() {
    const response = await config.behaviors.user.refreshSession(
      this.refreshToken,
    );

    return new Auth({
      userId: this.user.id,
      accessToken: response.token,
      refreshToken: response.refreshToken,
      expiry: response.expiresAt,
    });
  }

  public get asJson() {
    const expiryIso = this.expiry.toISO();
    invariant(!!expiryIso, "Invalid ISO date");

    return {
      userId: this.user.id,
      accessToken: this.accessToken,
      refreshToken: this.refreshToken,
      expiry: expiryIso,
    };
  }

  public static fromJson(json: unknown) {
    const { userId, accessToken, refreshToken, expiry } =
      Auth.schema.parse(json);

    return new Auth({
      userId,
      accessToken,
      refreshToken,
      expiry,
    });
  }
}
